import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Pagination as OriginalPagination } from '@frontend/design_system'
import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from './constants'
import { PaginationProps } from './types'
import { SP_PAGE, SP_PAGE_SIZE } from 'constants/searchParams'

export const Pagination = ({
  total,
  pageItemCount,
  pageSizeLabel,
  isShort = false,
  dataTestId,
  onPageChange,
  onPageSizeChange,
  withPageSizeDropdown = true,
  defaultPage = 1,
  defaultPageSize = DEFAULT_PAGE_SIZE,
}: PaginationProps) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const pendingPageSizeRef = useRef<string | null>(null)

  const currentPage = useMemo(() => {
    const page = searchParams.get(SP_PAGE)
    return page ? Math.max(1, parseInt(page, 10)) : defaultPage
  }, [searchParams, defaultPage])

  const currentPageSize = useMemo(() => {
    const size = searchParams.get(SP_PAGE_SIZE)
    return size && PAGE_SIZES.includes(size) ? size : defaultPageSize
  }, [searchParams, defaultPageSize])

  const updateParams = useCallback(
    (updates: Record<string, number | string>) => {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams)

        Object.entries(updates).forEach(([key, value]) => {
          newParams.set(key, value.toString())
        })

        return newParams
      })
    },
    [setSearchParams]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      const pageSizeToUse = pendingPageSizeRef.current ?? currentPageSize

      updateParams({
        [SP_PAGE]: newPage,
        [SP_PAGE_SIZE]: pageSizeToUse,
      })
      onPageChange?.(newPage)
      pendingPageSizeRef.current = null
    },
    [updateParams, onPageChange, currentPageSize]
  )

  const handlePageSizeChange = useCallback(
    (newPageSize: string) => {
      pendingPageSizeRef.current = newPageSize

      updateParams({
        [SP_PAGE_SIZE]: newPageSize,
        [SP_PAGE]: defaultPage,
      })

      onPageSizeChange?.(newPageSize)
    },
    [updateParams, defaultPage, onPageSizeChange]
  )

  const totalPages = Math.ceil(total / Number(currentPageSize))

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      handlePageChange(totalPages)
    }
  }, [currentPage, totalPages, handlePageChange])

  if (totalPages <= 1) {
    return null
  }

  return (
    <OriginalPagination
      changePage={handlePageChange}
      current={currentPage}
      total={total}
      pageSize={String(currentPageSize)}
      changePageSize={handlePageSizeChange}
      pageSizeLabel={pageSizeLabel}
      isShort={isShort}
      dataTestId={dataTestId}
      pageItemCount={pageItemCount}
      withPageSizeDropdown={withPageSizeDropdown}
    />
  )
}
